import { useEffect, useMemo, useState } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { trackPromise } from 'react-promise-tracker'
import { routes, privateTopLevelSegments, publicTopLevelSegments } from '../../routes/routes'
import api from '../../api'
import { applyWorkspaceSwitchResponse, classifyUrl, getCurrentSubsidiaryId, parseSubsidiaryFromPath, redirectWithSubsidiary } from '../../utils/subsidiary'
import { isAuthenticated } from '../../hooks/CookiesHook'
import { homeScreenRedirection, isSuccess, showError } from '../../utils/helper'
import PermissionDeniedModal from '../UI/PermissionDeniedModal'

type Outcome =
  | { kind: 'render'; basename: string }
  | { kind: 'redirecting' }
  | { kind: 'mismatch'; urlSubId: string; pathname: string; currentSubId: string | null }
  | { kind: 'denied' }

// Runs once per full page load (see plan: any URL a user can actually reach with a
// mismatched subsidiary segment already crossed a hard-navigation boundary to get
// there, so this covers back/forward re-validation without a separate listener).
const computeInitialOutcome = (): Outcome => {
  const { pathname } = window.location
  const currentSubId = getCurrentSubsidiaryId()
  const classification = classifyUrl(pathname, currentSubId, privateTopLevelSegments, publicTopLevelSegments)

  if (classification === 'public') return { kind: 'render', basename: '' }

  if (!isAuthenticated()) {
    window.location.replace('/login')
    return { kind: 'redirecting' }
  }

  if (classification === 'missing') {
    redirectWithSubsidiary(currentSubId, pathname)
    return { kind: 'redirecting' }
  }

  if (classification === 'match') {
    return { kind: 'render', basename: `/${currentSubId}` }
  }

  return { kind: 'mismatch', urlSubId: parseSubsidiaryFromPath(pathname), pathname, currentSubId }
}

const SubsidiaryBoundary = () => {
  const [outcome, setOutcome] = useState<Outcome>(computeInitialOutcome)

  useEffect(() => {
    if (outcome.kind !== 'mismatch') return
    const { urlSubId, pathname, currentSubId } = outcome

    const redirectHomeOnFailure = (err: any) => {
      showError(err)
      redirectWithSubsidiary(currentSubId, homeScreenRedirection())
    }

    trackPromise(
      api
        .getAfterAuthWorkspace()
        .then((res: any) => {
          const workspaces = Object.values(res?.data?.data || {}).flat() as any[]
          const target = workspaces.find((w) => w.subsidiary_master_id === urlSubId)

          if (!target) {
            setOutcome({ kind: 'denied' })
            return
          }

          return trackPromise(
            api
              .postChangeWorkspace({
                subsidiary_master_id: target.subsidiary_master_id,
                user_subsidiary_id: target.user_subsidiary_id,
                user_master_id: target.user_master_id,
              })
              .then((switchRes: any) => {
                if (isSuccess(switchRes)) {
                  applyWorkspaceSwitchResponse(switchRes?.data?.data, switchRes?.data?.meta, target.workspace_name, target.organization_name)
                  // pathname already carries the target subsidiary segment (that's what made this a
                  // mismatch) — re-prepending urlSubId here would double it, e.g. /8842/8842/dashboard.
                  window.location.replace(pathname)
                } else {
                  redirectHomeOnFailure(switchRes)
                }
              }),
          )
        })
        .catch(redirectHomeOnFailure),
    )
  }, [outcome])

  const router = useMemo(() => (outcome.kind === 'render' ? createBrowserRouter(routes, { basename: outcome.basename }) : null), [outcome])

  if (router) return <RouterProvider router={router} />
  if (outcome.kind === 'denied') return <PermissionDeniedModal open />
  return null // 'redirecting' or 'mismatch' — global FullScreenLoader covers the loading state
}

export default SubsidiaryBoundary
